import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { backendService } from "../../../services/backend.service";
import { getWeekStart } from "../../../utils/dateUtils";
import { resolveEffectiveWeekStartDay } from "../v2/resolveEffectiveWeekStartDay";
import { type CoverageSummaryResponse, CoverageTargetMode, type PeopleRotationResponse } from "./types";

/**
 * Prefix of the People-rotation query key. Exported so a write path can mark the roster stale: the
 * staffing grid mutates `HospitalDataProvider` state directly and re-renders, but this view reads a
 * cached query, so without an explicit invalidation a published add/remove left it showing the old
 * roster until a manual refetch.
 */
export const MASTER_SCHEDULE_PEOPLE_ROTATION_QUERY_KEY = "masterSchedulePeopleRotation";

/**
 * Reformats the backend shift-time label (e.g. "8am-4pm") into the prototype's
 * compact "8a–4p" idiom for visual consistency with the redesigned grid.
 *
 * Parses each side with Luxon (12-hour `h a` / `ha`), drops a `:00` top of the
 * hour, lowercases the meridiem to a single letter, and joins with an en-dash.
 * If a side can't be parsed we leave the original label untouched (fail-soft on
 * display only — no data is derived from this string).
 */
export const formatShiftLabel = (raw: string): string => {
	const parts = raw.split(/\s*[-–—]\s*/);
	if (parts.length !== 2) return raw;
	const compact = (token: string): string | null => {
		const trimmed = token.trim();
		const parsed = DateTime.fromFormat(trimmed.toLowerCase(), "h:mma").isValid
			? DateTime.fromFormat(trimmed.toLowerCase(), "h:mma")
			: DateTime.fromFormat(trimmed.toLowerCase(), "ha");
		if (!parsed.isValid) return null;
		const meridiem = parsed.hour < 12 ? "a" : "p";
		const display = parsed.minute === 0 ? parsed.toFormat("h") : parsed.toFormat("h:mm");
		return `${display}${meridiem}`;
	};
	const start = compact(parts[0]);
	const end = compact(parts[1]);
	if (start === null || end === null) return raw;
	return `${start}–${end}`;
};

/** Apply the display reformat to every row's shift label without touching any other field. */
const reformatShiftLabels = (response: CoverageSummaryResponse): CoverageSummaryResponse => ({
	...response,
	sections: response.sections.map((section) => ({
		...section,
		rows: section.rows.map((row) => ({ ...row, shift: formatShiftLabel(row.shift) })),
	})),
});

/**
 * Fetches the live Coverage + Summary grid for the chosen department.
 *
 * Mirrors `useFetchMasterSchedule` (v2): the query key is scoped by department +
 * the computed cycle-anchor start date + the runtime-week-start flag (so flipping
 * the flag refetches), and the query is `enabled` only once a department id is
 * present. The cycle anchor honors `ScheduleTemplateRuntimeWeekStart` exactly the
 * way v2 does — Sunday-anchored when the flag is off, dept `week_start_day` when on.
 */
// DEFAULT: census initial target; server downgrades to max and reports it via data.targetMode.
export const useMasterScheduleCoverage = (targetMode: CoverageTargetMode = CoverageTargetMode.Census) => {
	const { chosenDepartment, timezone } = useHospitalData();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const { viewStartDay } = useWeekIndex();
	const effectiveWeekStartDay = resolveEffectiveWeekStartDay(isRuntimeWeekStartEnabled, viewStartDay);

	const startDate = useMemo(() => getWeekStart(DateTime.now().setZone(timezone), effectiveWeekStartDay), [timezone, effectiveWeekStartDay]);

	const departmentId = chosenDepartment?.department_id;

	return useQuery({
		queryKey: ["masterScheduleCoverageSummary", departmentId, startDate.toISODate(), effectiveWeekStartDay, targetMode],
		queryFn: async (): Promise<CoverageSummaryResponse> => {
			// `enabled: !!departmentId` keeps this from firing while undefined; guard so the
			// service call sees a concrete `string` (and we fail loud if that invariant breaks).
			if (departmentId === undefined) throw new Error("masterScheduleCoverageSummary query ran without a department id");
			const response = await backendService.getMasterScheduleCoverageSummary(departmentId, startDate, targetMode);
			return reformatShiftLabels(response.data);
		},
		enabled: !!departmentId,
		refetchOnWindowFocus: false,
		placeholderData: keepPreviousData,
	});
};

/**
 * The People roster's week-0 anchor: today's week start under the DEPARTMENT's own `week_start_day`.
 *
 * Deliberately NOT gated on `ScheduleTemplateRuntimeWeekStart`, and deliberately not the shared
 * staffing `viewStartDay`. `build_people_rotation` snaps `start_date` with
 * `get_week_start_for_day(start_date, department.week_start_day)` and no flag of any kind, so the only
 * rule that can agree with the response's `cycleStart` is the department column. Gating this on the
 * flag anchored the window on Sunday while the response came back on (say) Monday: the range label
 * rendered Sun–Sat from the local anchor and then visibly jumped a day once the request landed.
 *
 * Exported because the roster's date navigation measures its week offset from this exact date. The
 * offset and the request have to agree to the day, and deriving the anchor twice — once here, once in
 * the view — is how they would come to disagree the next time either rule changes.
 *
 * Because the offset is measured from here, this value moving RE-FRAMES it: `week_start_day` arriving
 * late, or a department switch, changes which windows are reachable without changing the retained
 * offset. `StaffingPeopleView` therefore reconciles its offset against the new bounds during render,
 * before `useMasterSchedulePeopleRotation` is called — see the `reachableWeekOffset` clamp there.
 */
export const usePeopleCycleAnchor = (): DateTime => {
	const { chosenDepartment, timezone } = useHospitalData();
	const weekStartDay = chosenDepartment.week_start_day;

	return useMemo(() => getWeekStart(DateTime.now().setZone(timezone), weekStartDay), [timezone, weekStartDay]);
};

/**
 * Fetches the live People-axis rotation grid for the chosen department, `weekOffset` whole weeks from
 * the current cycle.
 *
 * The offset is a REQUEST parameter, not a client-side re-projection: `days` carries date-specific
 * PTO exceptions, per-day jobs, locations, shift spans and notes, and the endpoint's own contract
 * forbids tiling `cyclePattern` to fake a different span. `start_date` has always existed on this
 * endpoint (the backend snaps it to the department week start and projects the whole window from
 * there), so paging is one more parameterised fetch — `weekOffset === 0` is byte-for-byte the request
 * this hook has always made.
 *
 * Otherwise mirrors `useMasterScheduleCoverage`: the query key is scoped by department + the resolved
 * start date + the department's `week_start_day` (so a settings change refetches rather than serving a
 * window anchored on the old start), and the query is `enabled` only once a department id is
 * present. Deliberately no `keepPreviousData` — the grid's column dates come from the response's
 * `cycleStart`, so serving the previous window while the next one loads would show a date range that
 * disagrees with the columns under it. A page shows the loading surface instead.
 *
 * Hours are backend-computed (`nurse.hours`); the FE never re-derives them.
 */
export const useMasterSchedulePeopleRotation = (weekOffset: number) => {
	const { chosenDepartment } = useHospitalData();
	const currentCycleStart = usePeopleCycleAnchor();

	const startDate = useMemo(() => currentCycleStart.plus({ weeks: weekOffset }), [currentCycleStart, weekOffset]);

	const departmentId = chosenDepartment?.department_id;

	return useQuery({
		queryKey: [MASTER_SCHEDULE_PEOPLE_ROTATION_QUERY_KEY, departmentId, startDate.toISODate(), chosenDepartment.week_start_day],
		queryFn: async (): Promise<PeopleRotationResponse> => {
			if (departmentId === undefined) throw new Error("masterSchedulePeopleRotation query ran without a department id");
			const response = await backendService.getMasterSchedulePeopleRotation(departmentId, startDate);
			return response.data;
		},
		enabled: !!departmentId,
		refetchOnWindowFocus: false,
	});
};
